import React, { useState } from 'react';
import { Heart, Coffee, Gift } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

// Declare atlosDonate for TypeScript
declare global {
  interface Window {
    atlosDonate: {
      Donate: (params: {
        merchantId: string;
        orderId?: string;
        orderCurrency?: string;
        theme?: 'light' | 'dark';
      }) => void;
    };
  }
}

interface DonationButtonProps {
  variant?: 'primary' | 'secondary' | 'heart' | 'coffee';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showText?: boolean;
  onClick?: () => void;
}

const DonationButton: React.FC<DonationButtonProps> = ({
  variant = 'primary',
  size = 'md',
  className = '',
  showText = true,
  onClick
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDonation = () => {
    // If custom onClick is provided, use it instead
    if (onClick) {
      onClick();
      return;
    }

    if (!window.atlosDonate) {
      toast.error('Donation widget not loaded yet. Please try again in a moment.');
      return;
    }

    setIsLoading(true);
    
    try {
      window.atlosDonate.Donate({
        merchantId: process.env.REACT_APP_ATLOS_MERCHANT_ID || 'GT8LTIFOMN',
        orderId: `donation_${Date.now()}`,
        orderCurrency: 'USD',
        theme: 'dark' // Match your app's theme
      });
      
      toast.success('Thank you for supporting TypeRush! 💙');
    } catch (error) {
      console.error('Donation error:', error);
      toast.error('Failed to open donation window. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'heart':
        return 'bg-red-500 hover:bg-red-600 text-white';
      case 'coffee':
        return 'bg-amber-500 hover:bg-amber-600 text-white';
      case 'secondary':
        return 'bg-secondary hover:bg-accent/10 text-text border border-accent/20';
      default:
        return 'bg-accent hover:bg-accent/90 text-background';
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'lg':
        return 'px-6 py-3 text-lg';
      default:
        return 'px-4 py-2';
    }
  };

  const getIcon = () => {
    switch (variant) {
      case 'heart':
        return <Heart className="w-4 h-4" fill="currentColor" />;
      case 'coffee':
        return <Coffee className="w-4 h-4" />;
      default:
        return <Gift className="w-4 h-4" />;
    }
  };

  const getText = () => {
    switch (variant) {
      case 'heart':
        return 'Support Us';
      case 'coffee':
        return 'Buy us a Coffee';
      default:
        return 'Donate';
    }
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleDonation}
      disabled={isLoading}
      className={`
        inline-flex items-center gap-2 font-medium rounded-lg
        transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
        ${getVariantStyles()} ${getSizeStyles()} ${className}
      `}
    >
      {getIcon()}
      {showText && (
        <span>{isLoading ? 'Opening...' : getText()}</span>
      )}
    </motion.button>
  );
};

export default DonationButton;
